import { ReactNode } from 'react';

import {
  FlexAligner,
  ScaleColor,
  Typography,
  TypographyProps,
  type FlexAlignment,
  type FlexDirection,
} from '@scaleaq/scaleui';

import { responsive } from '@/utils';

import { StyledColorBar } from './dataRow/dataRow.styles.ts';
import { DataRowLabel } from './dataRow/DataRowLabel';
import { DataRowValue } from './dataRow/DataRowValue';

interface DataRowProps {
  color?: ScaleColor;
  isError?: boolean;
  isFetched?: boolean;
  isLoading?: boolean;
  label: string | ReactNode;
  secondaryLabel?: string | ReactNode;
  secondaryValue?: string | ReactNode;
  tertiaryLabel?: string | ReactNode;
  tertiaryValue?: string | ReactNode;
  title?: string;
  value?: string | ReactNode;
  itemDirection?: FlexDirection;
  itemAlignment?: FlexAlignment;
  labelType?: TypographyProps['type'];
  /** Smaller value and colour bar, for sub-rows nested under another row. */
  compact?: boolean;
}

export const DataRow = ({
  color,
  isError = false,
  isFetched = true,
  isLoading,
  label,
  secondaryLabel,
  secondaryValue,
  tertiaryLabel,
  tertiaryValue,
  title,
  value,
  itemDirection = 'row',
  itemAlignment = 'center',
  labelType = 'body_medium',
  compact = false,
}: DataRowProps) => (
  <FlexAligner direction='column'>
    {title && <Typography type='body_large'>{title}</Typography>}
    <FlexAligner gap={responsive.spacing.large} alignment={'center-left'}>
      {color && (
        <StyledColorBar orientation='vertical' color={color} height='2px' $compact={compact} />
      )}
      <FlexAligner direction='column'>
        <FlexAligner direction={itemDirection} spaceBetween alignment={itemAlignment}>
          <DataRowLabel value={label} type={labelType} />
          <DataRowValue
            isError={isError}
            isFetched={isFetched}
            isLoading={isLoading}
            type={compact ? 'heading_xsmall' : 'heading_medium'}
            value={value}
          />
        </FlexAligner>
        {(!!secondaryLabel || !!secondaryValue) && (
          <FlexAligner direction={itemDirection} spaceBetween alignment={itemAlignment}>
            <DataRowLabel value={secondaryLabel} type='body_small' />
            <DataRowValue
              isError={isError}
              isFetched={isFetched}
              isLoading={isLoading}
              type='body_large'
              value={secondaryValue}
            />
          </FlexAligner>
        )}
        {(!!tertiaryLabel || !!tertiaryValue) && (
          <FlexAligner direction={itemDirection} spaceBetween alignment={itemAlignment}>
            <DataRowLabel value={tertiaryLabel} type='body_small' />
            <DataRowValue
              isError={isError}
              isFetched={isFetched}
              isLoading={isLoading}
              type='body_large'
              value={tertiaryValue}
            />
          </FlexAligner>
        )}
      </FlexAligner>
    </FlexAligner>
  </FlexAligner>
);
